import { computed, inject, Injectable, signal } from "@angular/core";
import { combineLatest } from "rxjs";
import {
  AuthenticationService,
  AuthenticationState,
} from "./authentication.service";
import { AuthorizationService } from "./authorization.service";
import { Permission } from "./guards/permissions.enum";
import { UserDataService } from "./userData.service";
import {
  DataTransfersService,
  FileResponse,
  UserNameResponse,
} from "../services";

// Singleton so the "new files" badge is fetched once regardless of how many sidebar instances exist (mobile + desktop).
@Injectable({
  providedIn: "root",
})
export class DataTransferBadgeService {
  private readonly authService = inject(AuthenticationService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly userDataService = inject(UserDataService);
  private readonly filesService = inject(DataTransfersService);

  private readonly users = signal<UserNameResponse[]>([]);
  private readonly currentUserFiles = signal<FileResponse[]>([]);
  private readonly currentUserEmail = signal<string | null | undefined>(
    undefined
  );

  public readonly hasNewFiles = computed(
    () =>
      this.users().some((user) =>
        user.files?.some(
          (file) =>
            file.isNewFile && file.uploadedByEmail !== this.currentUserEmail()
        )
      ) ||
      this.currentUserFiles().some(
        (file) =>
          file.isNewFile && file.uploadedByEmail !== this.currentUserEmail()
      )
  );

  constructor() {
    combineLatest([
      this.authService.currentUser,
      this.userDataService.userUpdate$,
    ]).subscribe(([authResult]) => {
      if (
        authResult.state === AuthenticationState.LoggedOut ||
        !authResult.user
      ) {
        this.users.set([]);
        this.currentUserFiles.set([]);
        this.currentUserEmail.set(undefined);
        return;
      }

      this.currentUserEmail.set(authResult.user.email);

      if (
        this.authorizationService.hasPermission(Permission.ViewAllDataTransfer)
      ) {
        this.filesService
          .apiDataTransfersUsersGet()
          .subscribe((response) => this.users.set(response));
      } else {
        this.filesService
          .apiDataTransfersMeGet()
          .subscribe((response) =>
            this.currentUserFiles.set(response.items ?? [])
          );
      }
    });
  }
}
